import React from 'react';
import { Cpu, Zap, Car, Home, Battery, ShieldCheck, ArrowRight } from 'lucide-react';

const futureFeatures = [
  {
    icon: Cpu,
    title: "Smart Energy Management",
    description: "Intelligente Steuerung optimiert den Energiefluss und maximiert Ihren Eigenverbrauch automatisch.",
    color: "violet"
  },
  {
    icon: Zap,
    title: "Dynamische Stromtarife",
    description: "Bereit für variable Strompreise – laden Sie Speicher und E-Auto, wenn der Strom günstig ist.",
    color: "amber"
  },
  {
    icon: Car,
    title: "Bidirektionales Laden",
    description: "Vehicle-to-Grid und Vehicle-to-Home: Ihr E-Auto wird zum mobilen Stromspeicher für Ihr Zuhause.",
    color: "sun-blue"
  },
  {
    icon: Home,
    title: "Smart-Home-Integration",
    description: "Nahtlose Verbindung mit vorhandenen Smart-Home-Systemen für maximale Automatisierung.",
    color: "emerald"
  },
  {
    icon: Battery,
    title: "Zukünftige Batteriespeicher",
    description: "Systemdesign erlaubt einfache Erweiterung oder Austausch von Speicherkapazitäten.",
    color: "sun-orange"
  },
  {
    icon: ShieldCheck,
    title: "Backup-Lösungen",
    description: "Bei Bedarf kann Ihr Speicher über einen externen Generator nachgeladen werden – für außergewöhnliche Situationen.",
    color: "cyan"
  }
];

const colorMap = {
  'violet': { bg: 'bg-violet-100', icon: 'text-violet-600', dark: 'bg-violet-500' },
  'amber': { bg: 'bg-amber-100', icon: 'text-amber-600', dark: 'bg-amber-500' },
  'sun-blue': { bg: 'bg-sun-blue/10', icon: 'text-sun-blue', dark: 'bg-sun-blue' },
  'emerald': { bg: 'bg-emerald-100', icon: 'text-emerald-600', dark: 'bg-emerald-500' },
  'sun-orange': { bg: 'bg-sun-orange/10', icon: 'text-sun-orange', dark: 'bg-sun-orange' },
  'cyan': { bg: 'bg-cyan-100', icon: 'text-cyan-600', dark: 'bg-cyan-500' }
};

const FutureReadySection = () => {
  const scrollToContact = () => {
    const kontaktElement = document.querySelector('#kontakt');
    if (kontaktElement) {
      kontaktElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section data-testid="future-ready-section" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold mb-4">
            Zukunftssicher
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Zukunftssichere
            <span className="text-sun-orange"> Energiesysteme</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Sunprojects plant Photovoltaiksysteme, die für zukünftige Entwicklungen im Energiemarkt 
            vorbereitet sind – heute investieren, morgen profitieren.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {futureFeatures.map((feature, index) => {
            const IconComponent = feature.icon;
            const colors = colorMap[feature.color];
            
            return (
              <div
                key={index}
                data-testid={`future-feature-${index}`}
                className="group bg-white rounded-2xl p-8 border border-slate-100 hover:border-sun-orange/30 hover:shadow-xl hover:shadow-sun-orange/5 transition-all duration-300"
              >
                <div className={`w-14 h-14 ${colors.bg} rounded-2xl flex items-center justify-center mb-5 group-hover:${colors.dark} group-hover:scale-110 transition-all duration-300`}>
                  <IconComponent className={`w-7 h-7 ${colors.icon} group-hover:text-white transition-colors`} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="bg-gradient-to-r from-violet-50 via-purple-50 to-violet-50 border border-violet-200 rounded-2xl p-8 lg:p-10">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 bg-violet-100 rounded-2xl flex items-center justify-center">
                <ShieldCheck className="w-10 h-10 text-violet-600" />
              </div>
            </div>
            <div className="text-center lg:text-left flex-1">
              <h4 className="text-xl font-bold text-slate-900 mb-3">
                Vorbereitet auf außergewöhnliche Situationen
              </h4>
              <p className="text-slate-600 leading-relaxed">
                Moderne Photovoltaiksysteme können so konzipiert werden, dass der Batteriespeicher 
                in besonderen Situationen – wie längeren Netzausfällen – über einen externen Generator 
                nachgeladen werden kann. Dies bietet zusätzliche Sicherheit für Eigenheimbesitzer.
              </p>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={scrollToContact}
                className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-6 py-3 rounded-full font-semibold transition-all hover:scale-105"
              >
                Mehr erfahren
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FutureReadySection;
